import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { toast } from "react-toastify";
import PopupModal from "../shared/PopupModal";
import MailSettingsSection from "./MailSettingsSection";
import { useIsDesktop } from "../shared/hooks/useIsDesktop";
import { MAIL_MAGAZINE_OPTIONS } from "../../constants/mailMagazineOptions";
import { useNavigate } from "react-router-dom";
import { handleUnauthorizedOrRedirect } from "../utils/responseHandler";

// メール配信設定の初期値
const defaultMailMagazineStatus = {
  subscribed_to_project: true,
  subscribed_to_useful_info: true,
  subscribed_to_notice: true,
};

const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");

const unauthorizedRedirectOptions = {
  preserveUnauthorizedToastMessage:
    "セッションが切れました。ログインし直してください。",
};

const MailSubscriptionPopup = ({ shouldOpen }) => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailRegistered, setEmailRegistered] = useState(false);
  const [mailMagazineStatus, setMailMagazineStatus] = useState(
    defaultMailMagazineStatus,
  );

  // 設定画面へ遷移（表示済みフラグを更新）
  const onShowSettingsClick = async () => {
    const updated = await handleClose();

    if (!updated) return;

    navigate("/settings");
  };

  useEffect(() => {
    let canceled = false;

    if (!shouldOpen) {
      setOpen(false);
      return () => {
        canceled = true;
      };
    }

    // ポップアップ表示に必要な情報のみ取得
    const fetchMailMagazineSubscription = async () => {
      try {
        const response = await fetch("/api/users/mailmagazine_subscriptions", {
          headers: { Accept: "application/json" },
        });

        if (
          handleUnauthorizedOrRedirect(response, unauthorizedRedirectOptions)
        ) {
          return;
        }

        if (!response.ok) {
          throw new Error("mail magazine subscription fetch failed");
        }

        const data = await response.json();
        if (canceled) return;

        setEmailRegistered(data.email_registered === true);
        setOpen(data.show_mail_subscription_popup === true);
      } catch (e) {
        if (canceled) return;
        console.error(e);
        setOpen(false);
      }
    };

    fetchMailMagazineSubscription();

    return () => {
      canceled = true;
    };
  }, [shouldOpen]);

  // メール配信設定・表示済みフラグを更新
  const updateMailMagazineSubscription = async (userParams) => {
    const response = await fetch("/api/users/mailmagazine_subscriptions", {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify({ user: userParams }),
    });

    if (handleUnauthorizedOrRedirect(response, unauthorizedRedirectOptions)) {
      setOpen(false);
      return false;
    }

    if (!response.ok) {
      throw new Error("mail magazine subscription update failed");
    }

    return true;
  };

  // ポップアップを閉じる（表示済みフラグのみ更新）
  const handleClose = async () => {
    if (isSubmitting) return false;

    setIsSubmitting(true);
    setOpen(false);

    try {
      const updated = await updateMailMagazineSubscription({
        mail_subscription_popup_shown_flag: true,
      });

      return updated;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // チェック状態のみ更新（保存は「設定する」押下時）
  const handleChangeMailMagazineStatus = (category, _label, checked) => {
    setMailMagazineStatus((prev) => ({
      ...prev,
      [category]: checked,
    }));
  };

  // メール配信設定を保存
  const handleConfirm = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const updated = await updateMailMagazineSubscription({
        ...mailMagazineStatus,
        mail_subscription_popup_shown_flag: true,
      });

      if (!updated) return;

      setOpen(false);
      toast.success("メール配信設定を保存しました。");
    } catch (e) {
      console.error(e);
      toast.error("メール設定の保存に失敗しました。");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PopupModal
      open={open}
      title="メール設定"
      confirmLabel={emailRegistered ? "設定する" : "メールアドレスを設定する"}
      cancelLabel="あとで設定する"
      onConfirm={emailRegistered ? handleConfirm : onShowSettingsClick}
      onClose={handleClose}
      isSubmitting={isSubmitting}
    >
      <Typography sx={{ mb: 2, fontSize: "14px", whiteSpace: "pre-line" }}>
        新着案件やお役立ち情報をお届けするため、メール配信は初期設定で受信する設定になっています。
        {"\n"}
        設定は後からマイページで変更できます。
        {"\n"}※
        配信設定にかかわらず、重要なお知らせはすべてのユーザーに配信されます。
      </Typography>

      <MailSettingsSection
        emailRegistered={emailRegistered}
        isDesktop={isDesktop}
        sectionStyle={{ boxShadow: "none", p: 0 }}
        sectionTitleStyle={{ display: "none" }}
        sectionContentStyle={{ mt: 0 }}
        mailMagazineOptions={MAIL_MAGAZINE_OPTIONS}
        mailMagazineStatus={mailMagazineStatus}
        isSubmittingMailMagazine={isSubmitting}
        handleChangeMailMagazineStatus={handleChangeMailMagazineStatus}
        hiddenHelp={true}
      />
    </PopupModal>
  );
};

export default MailSubscriptionPopup;
